import { ConfigurationContext, EventPublication } from '@news-mono/web-common'
import {
    BlockV4DTO,
    Block_TextV4DTO,
    EventPostV4DTO,
} from '@west-australian-newspapers/publication-types'
import { useContext } from 'react'
import type { BlogPosting, ImageObject, LiveBlogPosting } from 'schema-dts'
import { useSectionMetaInfo } from '../../..'

export const useLiveBlogPostingSchema = (
    publication: EventPublication,
    activePosts: EventPostV4DTO[],
): { structuredData: LiveBlogPosting } => {
    const { Title, Hostname } = useSectionMetaInfo()
    const { publicUrl: configPublicUrl } = useContext(ConfigurationContext)

    const publicUrl = Hostname ? `https://${Hostname}` : configPublicUrl

    const structuredData = getLiveBLogPostingStructuredData(
        publication,
        activePosts,
        Title,
        publicUrl,
    )

    return { structuredData }
}

export const getLiveBLogPostingStructuredData = (
    publication: EventPublication,
    activePosts: EventPostV4DTO[],
    siteName: string,
    publicUrl: string,
): LiveBlogPosting => {
    const liveBlogUpdate = activePosts.map<BlogPosting>((post, i) => {
        return {
            '@type': 'BlogPosting',
            headline: post.title || undefined,
            datePublished: post.created,
            url: post._self,
            articleBody: getTextFromBlocks(post.content.blocks as BlockV4DTO[]),
        }
    })

    const coverageEndTimeDate = new Date(publication.publicationDate)
    // We don't have anything like a coverageEndTime property so just adding 22 hours to the publicationDate, which is what nbcnews does on their live blogs.
    // Google allows adding live updates even after the coverage end date
    coverageEndTimeDate.setHours(coverageEndTimeDate.getHours() + 22)

    const heroImage = publication.heroImage?.crops['16:9']

    const image: ImageObject | undefined = heroImage
        ? {
              '@type': 'ImageObject',
              url: heroImage.reference,
              width: String(heroImage.width),
              height: String(heroImage.height),
          }
        : undefined

    const structuredData: LiveBlogPosting & { '@context': string } = {
        '@context': 'https://schema.org',
        '@type': 'LiveBlogPosting',
        url: publication._self,
        coverageStartTime: publication.publicationDate,
        coverageEndTime: coverageEndTimeDate.toISOString(),
        datePublished: publication.publicationDate,
        dateModified: publication.lastUpdated,
        headline: publication.heading,
        description: publication.teaser,
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': publication._self,
        },
        author: {
            '@type': 'NewsMediaOrganization',
            name: siteName,
            url: publicUrl,
        },
        about: {
            '@type': 'Event',
            startDate: publication.publicationDate,
            name: publication.heading,
            location: 'Australia',
        },
        image,
        liveBlogUpdate,
    }

    return structuredData
}

export const getTextFromBlocks = (blocks: BlockV4DTO[]) => {
    const textBlocks = blocks.filter(
        (block) => block.hasOwnProperty('kind') && block.kind === 'text',
    )
    const mappedTextBlocks = textBlocks.map(
        (block) => (block as Block_TextV4DTO).text,
    )
    return mappedTextBlocks.join(' ')
}
